/**
 * AdminTenantsBulkModal — modal akcji masowej (F8 T8.4b).
 *
 * Akcje:
 *  - suspend       — wymaga reason 5-500 znakow
 *  - activate      — tylko potwierdzenie
 *  - extend_trial  — wymaga daysToAdd 1-90 dni
 *
 * Po POST /api/admin/tenants/bulk dostajemy {successes, failures}. W przypadku
 * partial failure pokazujemy ekran wynikow z lista bledow per tenant; gdy 100%
 * sukcesu — od razu zamykamy z toastem (parent invalidate).
 */

import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { api } from '@/lib/api';
import type { TenantListItem } from '@/pages/AdminTenants';

export type BulkAction = 'suspend' | 'activate' | 'extend_trial';

export interface AdminTenantsBulkModalProps {
  action: BulkAction;
  tenants: TenantListItem[];
  onClose: () => void;
  onDone: () => void;
}

interface BulkSuccessRow {
  tenantId: string;
  affectedSubscriptions: number;
}

interface BulkFailureRow {
  tenantId: string;
  error: string;
  message?: string;
}

interface BulkResponse {
  successes: BulkSuccessRow[];
  failures: BulkFailureRow[];
}

export function AdminTenantsBulkModal({
  action,
  tenants,
  onClose,
  onDone,
}: AdminTenantsBulkModalProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [daysToAdd, setDaysToAdd] = useState('14');
  const [submitting, setSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkResponse | null>(null);

  const reasonTrimmed = reason.trim();
  const reasonValid = action !== 'suspend' || reasonTrimmed.length >= 5;
  const daysParsed = Number(daysToAdd);
  const daysValid =
    action !== 'extend_trial' ||
    (Number.isFinite(daysParsed) && daysParsed >= 1 && daysParsed <= 90);
  const canSubmit =
    !submitting && tenants.length > 0 && tenants.length <= 100 && reasonValid && daysValid;

  const variant: 'danger' | 'primary' = action === 'suspend' ? 'danger' : 'primary';

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setGlobalError(null);
    setSubmitting(true);

    const body: Record<string, unknown> = {
      tenantIds: tenants.map((t) => t.id),
      action,
    };
    if (action === 'suspend') body.reason = reasonTrimmed;
    if (action === 'extend_trial') body.daysToAdd = daysParsed;

    try {
      const res = await api.post<BulkResponse>('/admin/tenants/bulk', body);
      setResult(res.data);
      // 100% sukcesu — zamykamy od razu, parent zrobi toast.
      if (res.data.failures.length === 0) {
        onDone();
      }
    } catch (err) {
      const axErr = err as AxiosError<{ error?: string }>;
      const code = axErr.response?.data?.error;
      if (axErr.response?.status === 422 && code) {
        setGlobalError(t(`admin.tenants.bulk.errors.${code}`));
      } else {
        setGlobalError(t('admin.tenants.bulk.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Ekran wyniku z partial failure.
  if (result && result.failures.length > 0) {
    return (
      <Modal
        open
        onClose={onDone}
        title={t('admin.tenants.bulk.resultTitle')}
        description={t('admin.tenants.bulk.resultDescription', {
          successes: result.successes.length,
          failures: result.failures.length,
        })}
        closeOnBackdrop
        closeOnEsc
        footer={
          <Button variant="primary" onClick={onDone}>
            {t('admin.tenants.bulk.resultClose')}
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          {result.successes.length > 0 && (
            <p className="text-sm text-emerald-700 dark:text-emerald-300">
              {t('admin.tenants.bulk.resultSuccessCount', {
                count: result.successes.length,
              })}
            </p>
          )}
          <div>
            <p className="mb-2 text-sm font-medium text-text-secondary">
              {t('admin.tenants.bulk.resultFailureHeader')}
            </p>
            <ul className="flex max-h-60 flex-col gap-1 overflow-y-auto rounded-lg border border-border-primary bg-bg-secondary p-3 text-sm">
              {result.failures.map((f) => {
                const tenant = tenants.find((t) => t.id === f.tenantId);
                const errKey = `admin.tenants.bulk.failureCodes.${f.error}`;
                const translated = t(errKey);
                const errLabel = translated === errKey ? f.error : translated;
                return (
                  <li
                    key={f.tenantId}
                    className="flex items-baseline justify-between gap-3 border-b border-border-secondary py-1 last:border-b-0"
                  >
                    <span className="font-medium text-text-primary">
                      {tenant ? `${tenant.name} (${tenant.code})` : f.tenantId}
                    </span>
                    <span className="text-xs text-red-600 dark:text-red-400">{errLabel}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={() => (submitting ? undefined : onClose())}
      title={t(`admin.tenants.bulk.titles.${action}`, { count: tenants.length })}
      description={t(`admin.tenants.bulk.descriptions.${action}`)}
      closeOnBackdrop={!submitting}
      closeOnEsc={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('admin.tenants.bulk.cancel')}
          </Button>
          <Button
            type="submit"
            form="bulk-action-form"
            variant={variant}
            loading={submitting}
            disabled={!canSubmit}
          >
            {submitting
              ? t('admin.tenants.bulk.submitting')
              : t(`admin.tenants.bulk.confirm.${action}`)}
          </Button>
        </>
      }
    >
      <form id="bulk-action-form" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <div>
          <p className="mb-2 text-sm font-medium text-text-secondary">
            {t('admin.tenants.bulk.tenantsListHeader', { count: tenants.length })}
          </p>
          <ul
            aria-label={t('admin.tenants.bulk.tenantsListLabel')}
            className="flex max-h-40 flex-col gap-1 overflow-y-auto rounded-lg border border-border-primary bg-bg-secondary p-3 text-sm"
          >
            {tenants.map((tn) => (
              <li
                key={tn.id}
                className="flex items-baseline justify-between gap-3 border-b border-border-secondary py-1 last:border-b-0"
              >
                <span className="text-text-primary">{tn.name}</span>
                <span className="font-mono text-xs text-text-muted">{tn.code}</span>
              </li>
            ))}
          </ul>
        </div>

        {action === 'suspend' && (
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-text-secondary">
              {t('admin.tenants.bulk.reasonLabel')}
            </span>
            <textarea
              className="rounded-lg border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-text-primary focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500 dark:border-border-primary dark:bg-bg-tertiary"
              rows={3}
              maxLength={500}
              required
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
              placeholder={t('admin.tenants.bulk.reasonPlaceholder')}
            />
            <span className="text-xs text-text-muted">
              {t('admin.tenants.bulk.reasonHint', { count: reasonTrimmed.length })}
            </span>
          </label>
        )}

        {action === 'extend_trial' && (
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-text-secondary">
              {t('admin.tenants.bulk.daysToAddLabel')}
            </span>
            <input
              type="number"
              min={1}
              max={90}
              required
              autoFocus
              className="rounded-lg border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-text-primary focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500 dark:border-border-primary dark:bg-bg-tertiary"
              value={daysToAdd}
              onChange={(e) => setDaysToAdd(e.target.value)}
              disabled={submitting}
            />
            <span className="text-xs text-text-muted">{t('admin.tenants.bulk.daysToAddHint')}</span>
          </label>
        )}

        {globalError && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
          >
            {globalError}
          </div>
        )}
      </form>
    </Modal>
  );
}
